/**
 * Per-car engagement emits for the inventory Insights tab (#970, Workstream A2).
 *
 * These fire over the browser-control WebSocket as `user-action` events,
 * exactly like the existing `car-view` emit in CarCard — so they land in
 * `browser_control_events` with `event_type='user-action'`, `payload.action`,
 * `payload.source='user'`, and `payload.data.{carId,make,model,...}`. The
 * backend (`InventoryAnalyticsService`) aggregates them by EXACT `carId`.
 *
 * Distinct from `analyticsBridge` (GTM dataLayer + sg_ beacons): that layer is
 * for the client's GA/GTM. WhatsApp / Telegram keep their `sg_*_click` beacons
 * there; here we add the per-car attribution our own analytics needs.
 *
 * `sendUserAction` is a no-op when the WS isn't connected — emits are
 * best-effort and never block the UI (same contract as car-view).
 */
import { browserControlWS } from './browserControlWebSocket';

/**
 * CTA sub-types. MUST stay in lockstep with the backend `CtaType` vocabulary
 * in `apps/b2b-api/.../browser-control/analytics/browser-event.helpers.ts`.
 */
export const CtaType = {
  TEST_DRIVE: 'test_drive',
  CHECK_AVAILABILITY: 'check_availability',
  BF_FINANCE: 'bf_finance',
  ASK_AI: 'ask_ai',
  SHARE: 'share',
};

/** Module-level "car currently being viewed", set by the car-detail route.
 *  Lets the floating widget attribute a chat-init beacon to the open car. */
let currentCar = null;

/**
 * @param {{ id: (string|number), make?: string, model?: string }|null} car
 */
export function setCurrentCar(car) {
  currentCar = car && car.id != null
    ? { carId: String(car.id), make: car.make ?? null, model: car.model ?? null }
    : null;
}

/** @returns {{ carId: string, make: string|null, model: string|null }|null} */
export function getCurrentCar() {
  return currentCar;
}

/**
 * Emit a per-car CTA click (Check Availability / Ask AI / Finance / Test Drive).
 * @param {{ id: (string|number), make?: string, model?: string }} car
 * @param {string} ctaType - one of `CtaType`
 */
export function emitCtaClick(car, ctaType) {
  if (!car || car.id == null) return;
  browserControlWS.sendUserAction(
    'cta-click',
    {
      carId: String(car.id),
      make: car.make ?? null,
      model: car.model ?? null,
      ctaType,
    },
    false,
  );
}

/**
 * Emit a per-car WhatsApp click for the car currently on screen (if any).
 * Called from the single `openWhatsApp()` entry point; the GTM
 * `sg_whatsapp_click` beacon stays there. No-op when no car is in context
 * (e.g. WhatsApp from the contact page or home).
 */
export function emitWhatsAppCarClick() {
  if (!currentCar) return;
  browserControlWS.sendUserAction(
    'whatsapp_click',
    {
      carId: currentCar.carId,
      make: currentCar.make,
      model: currentCar.model,
    },
    false,
  );
}

/**
 * Emit a per-car Telegram click for the car currently on screen (if any) —
 * mirror of `emitWhatsAppCarClick` (#1493). Called from the single
 * `openTelegram()` entry point; the GTM `sg_telegram_click` beacon stays there.
 * No-op when no car is in context.
 */
export function emitTelegramCarClick() {
  if (!currentCar) return;
  browserControlWS.sendUserAction(
    'telegram_click',
    {
      carId: currentCar.carId,
      make: currentCar.make,
      model: currentCar.model,
    },
    false,
  );
}
